import styled from "@emotion/styled";
import { PublicKey } from "@solana/web3.js";
import type { TokenInfo } from "@stepfinance/common";
import { black } from "@stepfinance/react";
import memoize from "memoize-one";
import type { FC } from "react";
import { memo } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import type { ListChildComponentProps } from "react-window";
import { areEqual, FixedSizeList as List } from "react-window";

import type { AmountInputProps } from "../AmountInput/AmountInput";
import type { UserBalancesMap } from "../Trade/SwapTokenSelectModal";
import { TokenListRow } from "./TokenListRow";

export type TokenListProps = Pick<AmountInputProps, "disabledMintFromTokenList"> & {
  mint: string | PublicKey;
  onMintSelected: (mintAddress: string) => void;
  searchValue: string;
  swappableTokens: TokenInfo[];
  userBalancesMap: UserBalancesMap;
};

const createTokenItemData = memoize(
  (
    filteredSwappableTokens: TokenInfo[],
    mint: TokenListProps["mint"],
    dependentMint: TokenListProps["disabledMintFromTokenList"],
    onMintSelected: TokenListProps["onMintSelected"],
    userBalancesMap: TokenListProps["userBalancesMap"],
  ) => ({
    filteredSwappableTokens,
    mint,
    dependentMint,
    onMintSelected,
    userBalancesMap,
  }),
);

const Row: FC<ListChildComponentProps<ReturnType<typeof createTokenItemData>>> = memo(({ index, style, data }) => {
  const { dependentMint, filteredSwappableTokens, mint, onMintSelected, userBalancesMap } = data;
  const { address, symbol, decimals, name } = filteredSwappableTokens[index];

  return (
    <div style={style}>
      <TokenListRow
        name={name}
        decimals={decimals}
        key={address}
        address={address}
        symbol={symbol}
        selected={mint instanceof PublicKey ? mint.toBase58() === address : mint === address}
        balance={userBalancesMap[address]?.tokenAmount.uiAmount}
        onMintSelected={onMintSelected}
        disabled={dependentMint ? address === dependentMint.toBase58() : false}
      />
    </div>
  );
}, areEqual);
Row.displayName = "Row";

export const TokenList: FC<TokenListProps> = ({
  disabledMintFromTokenList,
  mint,
  onMintSelected,
  searchValue,
  swappableTokens,
  userBalancesMap,
}) => {
  const filteredSwappableTokens = swappableTokens.filter(
    ({ name, symbol }) => symbol.toLowerCase().startsWith(searchValue) || name.toLowerCase().startsWith(searchValue),
  );

  const itemData = createTokenItemData(
    filteredSwappableTokens,
    mint,
    disabledMintFromTokenList,
    onMintSelected,
    userBalancesMap,
  );

  return (
    <BaseTokenList>
      <AutoSizer>
        {({ height, width }) => (
          <List
            height={height}
            width={width}
            itemCount={filteredSwappableTokens.length}
            itemData={itemData}
            itemSize={60}
            itemKey={(index) => filteredSwappableTokens[index].address}
            overscanCount={50}
          >
            {Row}
          </List>
        )}
      </AutoSizer>
    </BaseTokenList>
  );
};

const BaseTokenList = styled.div`
  margin-top: 10px;
  height: 378px;
  background-color: ${black};
`;
